import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Checkbox } from '@/components/ui/checkbox';
import { Search, ChevronDown, X, SlidersHorizontal, Filter } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  TRIGGER,
  SEARCH_ICON,
  CONTROL,
  OPTION_ROW,
  activeCls,
  ControlLabel,
  CONTROL_DECK,
  CONTROL_DECK_ENTER,
  SECTION_LABEL
} from '@/components/ui/control-deck';

/** The structural + text filter values shared by the map and list pages. */
export interface RecallFilterValues {
  vin: string;
  make: string;
  model: string;
  year: string;
  dealer: string;
  campaigns: string[];
  city: string;
  zip: string;
  mobile_eligible: string;
  campaign_status: string;
  priority: string;
  assignment_state: string;
}

export interface RecallFilterBarProps {
  values: RecallFilterValues;
  /** Patch one or more filter values; the page merges into its own state. */
  onChange: (patch: Partial<RecallFilterValues>) => void;
  zipRadius: string;
  onZipRadiusChange: (value: string) => void;
  selectedTechnicianIds: string[];
  onTechniciansChange: (ids: string[]) => void;
  colorByTechnician: boolean;
  onColorByTechnicianChange: (value: boolean) => void;
  // Option lists
  campaignsOptions: string[];
  cityOptions: string[];
  zipOptions: string[];
  technicians: Array<{ id: string; full_name: string }>;
  getTechnicianColor: (id: string) => string;
  // Active count + clear
  activeFilterCount: number;
  onClearAll: () => void;
  /** Optional right-side content (e.g. the list page's result count). The map
   *  uses an on-map stats chip instead and passes nothing. */
  statsText?: string;
  loadingIndicator?: React.ReactNode;
  /** Show the technician filter control. */
  showTechnicianControls?: boolean;
  /** Show the "Color by technician" toggle (map only; meaningless on the list). */
  showColorByTechnician?: boolean;
}

const ASSIGNMENT_LABELS: Record<string, string> = {
  unassigned: 'Unassigned',
  in_route: 'Added to a route'
};

/**
 * Compact, persistent filter bar for the recall map and list. Common filters are
 * inline (one click, no drawer gate); rarely-used fields (Make/Model/Year/Dealer/
 * ZIP/Radius/Skill/City) live in an expandable "Advanced" drawer. Owns only its
 * own popover/search UI state; all filter values are controlled by the parent.
 */
/* eslint-disable max-lines-per-function, complexity */
export const RecallFilterBar: React.FC<RecallFilterBarProps> = ({
  values,
  onChange,
  zipRadius,
  onZipRadiusChange,
  selectedTechnicianIds,
  onTechniciansChange,
  colorByTechnician,
  onColorByTechnicianChange,
  campaignsOptions,
  cityOptions,
  zipOptions,
  technicians,
  getTechnicianColor,
  activeFilterCount,
  onClearAll,
  statsText,
  loadingIndicator,
  showTechnicianControls = true,
  showColorByTechnician = true
}) => {
  const [campaignOpen, setCampaignOpen] = useState(false);
  const [campaignTerm, setCampaignTerm] = useState('');
  const [cityOpen, setCityOpen] = useState(false);
  const [cityTerm, setCityTerm] = useState('');
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // Technician now lives in the Advanced drawer, so it counts toward "advanced".
  const advancedActive = Boolean(
    values.make ||
      values.model ||
      values.year ||
      values.dealer ||
      values.zip !== 'all' ||
      zipRadius !== '0' ||
      values.city !== 'all' ||
      values.priority !== 'all' ||
      selectedTechnicianIds.length > 0
  );

  // Value-pill text for the Campaign control: one selection shows its name,
  // multiples show a count, nothing shows no pill.
  const campaignValue = !values.campaigns.length
    ? undefined
    : values.campaigns.length === 1
      ? values.campaigns[0]
      : `${values.campaigns.length} selected`;

  const toggleCampaign = (campaign: string, checked: boolean) => {
    const next = checked
      ? [...values.campaigns, campaign]
      : values.campaigns.filter(c => c !== campaign);
    onChange({ campaigns: next });
  };

  const toggleTechnician = (id: string, checked: boolean) => {
    onTechniciansChange(
      checked ? [...selectedTechnicianIds, id] : selectedTechnicianIds.filter(t => t !== id)
    );
  };

  return (
    <div
      className={cn(
        // Floating, elevated control deck so the bar reads as layered above the map.
        'flex flex-wrap items-center gap-2 px-3 py-2 flex-shrink-0 m-2',
        CONTROL_DECK,
        CONTROL_DECK_ENTER
      )}
    >
      {/* Section label */}
      <div className={SECTION_LABEL}>
        <Filter className='h-4 w-4' />
        <span className='hidden sm:inline'>Filters</span>
      </div>

      {/* Quick search (VIN/dealer) */}
      <div className='relative'>
        <Search className={SEARCH_ICON} />
        <Input
          placeholder='Search VIN...'
          className={cn('pl-8 h-8 text-sm w-40 sm:w-56', CONTROL)}
          value={values.vin}
          onChange={e => onChange({ vin: e.target.value })}
        />
      </div>

      {/* Campaign multi-select */}
      <Popover open={campaignOpen} onOpenChange={setCampaignOpen}>
        <PopoverTrigger asChild>
          <Button
            variant='outline'
            className={cn(
              TRIGGER,
              'justify-between font-normal',
              CONTROL,
              activeCls(values.campaigns.length > 0)
            )}
          >
            <ControlLabel label='Campaign' value={campaignValue} />
            <ChevronDown className='h-4 w-4 shrink-0 opacity-50' />
          </Button>
        </PopoverTrigger>
        <PopoverContent className='w-64 max-h-[400px] p-0 z-[9999] flex flex-col' align='start'>
          <div className='px-3 py-2 border-b shrink-0'>
            <div className='relative'>
              <Search className='absolute left-2 top-2.5 h-4 w-4 text-muted-foreground' />
              <Input
                placeholder='Search campaigns...'
                value={campaignTerm}
                onChange={e => setCampaignTerm(e.target.value)}
                className='pl-8 h-8'
              />
            </div>
          </div>
          <div className='flex-1 overflow-y-auto p-2 min-h-0'>
            {campaignsOptions
              .filter(c => !campaignTerm || c.toLowerCase().includes(campaignTerm.toLowerCase()))
              .map(campaign => (
                <div
                  key={campaign}
                  role='button'
                  tabIndex={0}
                  className={OPTION_ROW}
                  onClick={() => toggleCampaign(campaign, !values.campaigns.includes(campaign))}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleCampaign(campaign, !values.campaigns.includes(campaign));
                    }
                  }}
                >
                  <Checkbox
                    checked={values.campaigns.includes(campaign)}
                    onClick={e => e.stopPropagation()}
                    onCheckedChange={checked => toggleCampaign(campaign, checked === true)}
                  />
                  <label className='text-sm flex-1 cursor-pointer truncate'>{campaign}</label>
                </div>
              ))}
          </div>
          {values.campaigns.length > 0 && (
            <div className='px-3 py-2 border-t bg-muted/30 shrink-0'>
              <button
                type='button'
                className='text-xs text-muted-foreground hover:text-foreground'
                onClick={() => onChange({ campaigns: [] })}
              >
                Clear selected
              </button>
            </div>
          )}
        </PopoverContent>
      </Popover>

      {/* Assignment state */}
      <Select
        value={values.assignment_state}
        onValueChange={value => onChange({ assignment_state: value })}
      >
        <SelectTrigger
          className={cn(TRIGGER, CONTROL, activeCls(values.assignment_state !== 'all'))}
        >
          <ControlLabel
            label='Assignment'
            value={
              values.assignment_state !== 'all'
                ? ASSIGNMENT_LABELS[values.assignment_state]
                : undefined
            }
          />
        </SelectTrigger>
        <SelectContent className='z-[9999]'>
          <SelectItem value='all'>All recalls</SelectItem>
          <SelectItem value='unassigned'>Unassigned</SelectItem>
          <SelectItem value='in_route'>Added to a route</SelectItem>
        </SelectContent>
      </Select>

      {/* Mobile eligible */}
      <Select
        value={values.mobile_eligible}
        onValueChange={value => onChange({ mobile_eligible: value })}
      >
        <SelectTrigger
          className={cn(TRIGGER, CONTROL, activeCls(values.mobile_eligible !== 'all'))}
        >
          <ControlLabel
            label='Repair location'
            value={
              values.mobile_eligible === 'all'
                ? undefined
                : values.mobile_eligible === 'true'
                  ? 'Mobile'
                  : 'In-shop only'
            }
          />
        </SelectTrigger>
        <SelectContent className='z-[9999]'>
          <SelectItem value='all'>Anywhere</SelectItem>
          <SelectItem value='true'>Mobile</SelectItem>
          <SelectItem value='false'>In-shop only</SelectItem>
        </SelectContent>
      </Select>

      {/* Campaign status */}
      <Select
        value={values.campaign_status}
        onValueChange={value => onChange({ campaign_status: value })}
      >
        <SelectTrigger
          className={cn(TRIGGER, CONTROL, activeCls(values.campaign_status !== 'all'))}
        >
          <ControlLabel
            label='Status'
            value={values.campaign_status !== 'all' ? values.campaign_status : undefined}
          />
        </SelectTrigger>
        <SelectContent className='z-[9999]'>
          <SelectItem value='all'>All status</SelectItem>
          <SelectItem value='Available'>Available</SelectItem>
          <SelectItem value='Advance Notice'>Advance Notice</SelectItem>
          <SelectItem value='Paused'>Paused</SelectItem>
        </SelectContent>
      </Select>

      {/* Advanced filters drawer (pushed to the right of the toolbar) */}
      <Sheet open={advancedOpen} onOpenChange={setAdvancedOpen}>
        <SheetTrigger asChild>
          <Button
            variant='outline'
            className={cn(
              TRIGGER,
              CONTROL,
              'group gap-1.5 ml-auto',
              // Orange accent when filters are set OR the drawer is open.
              activeCls(advancedActive || advancedOpen)
            )}
          >
            <SlidersHorizontal
              className={cn(
                'h-3.5 w-3.5 transition-transform duration-150',
                'group-hover:scale-110'
              )}
            />
            Advanced
            {advancedActive && <span className='h-1.5 w-1.5 rounded-full bg-primary' />}
          </Button>
        </SheetTrigger>
        <SheetContent side='right' className='w-full sm:max-w-md overflow-y-auto'>
          <SheetHeader>
            <SheetTitle>Advanced filters</SheetTitle>
          </SheetHeader>

          <div className='mt-4 space-y-6'>
            {/* Vehicle */}
            <div className='space-y-3'>
              <p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                Vehicle
              </p>
              <div className='grid grid-cols-2 gap-3'>
                <div className='flex flex-col gap-1.5'>
                  <p className='text-xs font-medium text-foreground'>Make</p>
                  <Input
                    placeholder='Make...'
                    className='h-8 text-sm'
                    value={values.make}
                    onChange={e => onChange({ make: e.target.value })}
                  />
                </div>
                <div className='flex flex-col gap-1.5'>
                  <p className='text-xs font-medium text-foreground'>Model</p>
                  <Input
                    placeholder='Model...'
                    className='h-8 text-sm'
                    value={values.model}
                    onChange={e => onChange({ model: e.target.value })}
                  />
                </div>
                <div className='flex flex-col gap-1.5'>
                  <p className='text-xs font-medium text-foreground'>Year</p>
                  <Input
                    placeholder='Year...'
                    className='h-8 text-sm'
                    value={values.year}
                    onChange={e => onChange({ year: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/* Location */}
            <div className='space-y-3'>
              <p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                Location
              </p>
              <div className='flex flex-col gap-1.5'>
                <p className='text-xs font-medium text-foreground'>Dealer</p>
                <Input
                  placeholder='Dealer...'
                  className='h-8 text-sm'
                  value={values.dealer}
                  onChange={e => onChange({ dealer: e.target.value })}
                />
              </div>
              <div className='flex flex-col gap-1.5'>
                <p className='text-xs font-medium text-foreground'>City</p>
                <Select
                  value={values.city}
                  open={cityOpen}
                  onOpenChange={setCityOpen}
                  onValueChange={value => {
                    onChange({ city: value });
                    setCityTerm('');
                  }}
                >
                  <SelectTrigger className='h-8 text-sm'>
                    <SelectValue placeholder='All Cities' />
                  </SelectTrigger>
                  <SelectContent className='max-h-[300px] z-[9999]'>
                    <div className='px-2 pb-2 sticky top-0 bg-background z-10 border-b'>
                      <div className='relative'>
                        <Search className='absolute left-2 top-2.5 h-4 w-4 text-muted-foreground' />
                        <Input
                          placeholder='Type to search...'
                          value={cityTerm}
                          onChange={e => setCityTerm(e.target.value)}
                          className='pl-8 h-8'
                          onClick={e => e.stopPropagation()}
                          onKeyDown={e => e.stopPropagation()}
                        />
                      </div>
                    </div>
                    <SelectItem value='all'>All Cities</SelectItem>
                    {cityOptions
                      .filter(
                        city => !cityTerm || city.toLowerCase().includes(cityTerm.toLowerCase())
                      )
                      .map(city => (
                        <SelectItem key={city} value={city}>
                          {city}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className='grid grid-cols-2 gap-3'>
                <div className='flex flex-col gap-1.5'>
                  <p className='text-xs font-medium text-foreground'>ZIP code</p>
                  <Select value={values.zip} onValueChange={value => onChange({ zip: value })}>
                    <SelectTrigger className='h-8 text-sm'>
                      <SelectValue placeholder='All ZIP Codes' />
                    </SelectTrigger>
                    <SelectContent className='max-h-[300px] z-[9999]'>
                      <SelectItem value='all'>All ZIP Codes</SelectItem>
                      {zipOptions.map(zip => (
                        <SelectItem key={zip} value={zip}>
                          {zip}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className='flex flex-col gap-1.5'>
                  <p className='text-xs font-medium text-foreground'>Radius</p>
                  <Select value={zipRadius} onValueChange={onZipRadiusChange}>
                    <SelectTrigger className='h-8 text-sm'>
                      <SelectValue placeholder='Exact match' />
                    </SelectTrigger>
                    <SelectContent className='z-[9999]'>
                      <SelectItem value='0'>Exact match</SelectItem>
                      <SelectItem value='5'>5 miles</SelectItem>
                      <SelectItem value='10'>10 miles</SelectItem>
                      <SelectItem value='15'>15 miles</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Recall */}
            <div className='space-y-3'>
              <p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                Recall
              </p>
              <div className='flex flex-col gap-1.5'>
                <p className='text-xs font-medium text-foreground'>Skill level</p>
                <Select
                  value={values.priority}
                  onValueChange={value => onChange({ priority: value })}
                >
                  <SelectTrigger className='h-8 text-sm'>
                    <SelectValue placeholder='All skill' />
                  </SelectTrigger>
                  <SelectContent className='z-[9999]'>
                    <SelectItem value='all'>All skill</SelectItem>
                    <SelectItem value='1'>High</SelectItem>
                    <SelectItem value='2'>Medium</SelectItem>
                    <SelectItem value='3'>Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Technician */}
            {showTechnicianControls && (
              <div className='space-y-3'>
                <div className='flex items-center justify-between'>
                  <p
                    className={cn(
                      'text-xs font-semibold uppercase tracking-wide',
                      'text-muted-foreground'
                    )}
                  >
                    Technician
                  </p>
                  {selectedTechnicianIds.length > 0 && (
                    <button
                      type='button'
                      className='text-xs text-muted-foreground hover:text-foreground'
                      onClick={() => onTechniciansChange([])}
                    >
                      Clear
                    </button>
                  )}
                </div>
                <div
                  className={cn(
                    'max-h-48 space-y-0.5 overflow-y-auto rounded-md border border-border p-1'
                  )}
                >
                  {technicians.length === 0 && (
                    <p className='px-2 py-1.5 text-xs text-muted-foreground'>
                      No technicians found
                    </p>
                  )}
                  {technicians.map(tech => {
                    const checked = selectedTechnicianIds.includes(tech.id);
                    return (
                      <div
                        key={tech.id}
                        role='button'
                        tabIndex={0}
                        className={OPTION_ROW}
                        onClick={() => toggleTechnician(tech.id, !checked)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleTechnician(tech.id, !checked);
                          }
                        }}
                      >
                        <Checkbox
                          checked={checked}
                          onClick={e => e.stopPropagation()}
                          onCheckedChange={isChecked =>
                            toggleTechnician(tech.id, isChecked === true)
                          }
                        />
                        <span
                          className='h-2.5 w-2.5 rounded-full shrink-0'
                          style={{ backgroundColor: getTechnicianColor(tech.id) }}
                        />
                        <label className='text-sm flex-1 cursor-pointer truncate'>
                          {tech.full_name}
                        </label>
                      </div>
                    );
                  })}
                </div>
                {showColorByTechnician && (
                  <div
                    role='button'
                    tabIndex={0}
                    className='flex items-center gap-2 text-xs text-muted-foreground cursor-pointer'
                    onClick={() => onColorByTechnicianChange(!colorByTechnician)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onColorByTechnicianChange(!colorByTechnician);
                      }
                    }}
                  >
                    <Checkbox
                      checked={colorByTechnician}
                      onClick={e => e.stopPropagation()}
                      onCheckedChange={checked => onColorByTechnicianChange(checked === true)}
                    />
                    <span>Color map by technician</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Clear all filters — spelled out so it's obvious; sits beside Advanced */}
      {activeFilterCount > 0 && (
        <Button
          variant='ghost'
          size='sm'
          onClick={onClearAll}
          title='Clear all filters'
          className={cn(
            'h-8 gap-1.5 px-2.5 text-xs font-medium shrink-0',
            'text-brand-primary-600 hover:bg-brand-primary-50 hover:text-brand-primary-700',
            'dark:text-brand-primary-400 dark:hover:bg-brand-primary-500/10',
            'dark:hover:text-brand-primary-300'
          )}
        >
          <X className='h-3.5 w-3.5' />
          Clear all filters
          <Badge
            key={activeFilterCount}
            className={cn(
              'h-4 px-1.5 text-[10px] border-transparent',
              'bg-brand-primary-100 text-brand-primary-900',
              'dark:bg-brand-primary-600 dark:text-brand-primary-50',
              // Brief pop whenever the active-filter count changes.
              'animate-in zoom-in-50 duration-200'
            )}
          >
            {activeFilterCount}
          </Badge>
        </Button>
      )}

      {/* Optional right-side slot (list page result count / loading) */}
      {(statsText || loadingIndicator) && (
        <div className='flex items-center gap-2 min-w-0'>
          {statsText && (
            <span className='text-xs text-muted-foreground font-medium truncate'>{statsText}</span>
          )}
          {loadingIndicator}
        </div>
      )}
    </div>
  );
};

export default RecallFilterBar;
